
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';
import { CustomProcess } from '../CustomProcess';
import { CustomCaseStudies } from '../CustomCaseStudies';
import {
  Calculator, Check, X, Loader2, MessageSquare, Database, Server, ShieldCheck, Lock, Minus, Plus, Cpu, ArrowRight, Activity, Terminal, Workflow, Network, GitBranch, Layers, Box, Settings, Bot, FileText, Mail, Zap, Users
} from 'lucide-react';
import { Page } from '../../types';

interface CustomArchitecturePageProps {
  onNavigate?: (page: Page) => void;
}

const WorkflowCoreVisual = () => {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState<{ id: number, x: number, y: number, active: boolean, type: string }[]>([
    { id: 0, x: 50, y: 50, active: true, type: 'CORE' },
    { id: 1, x: 20, y: 20, active: false, type: 'MAIL' },
    { id: 2, x: 80, y: 20, active: false, type: 'CRM' },
    { id: 3, x: 20, y: 80, active: false, type: 'DOCS' },
    { id: 4, x: 80, y: 80, active: false, type: 'CHAT' },
    { id: 5, x: 50, y: 20, active: false, type: 'API' },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNodes(curr => curr.map(n => ({ ...n, active: n.id === 0 ? true : Math.random() > 0.6 })));
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#0F172A] w-full h-full flex flex-col relative overflow-hidden p-6 md:p-10 border border-slate-700 rounded-xl shadow-2xl min-h-[400px]">
      <div className="flex justify-between items-center mb-8 border-b border-slate-700 pb-4 z-10 relative">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-clever-teal animate-pulse"></div>
          <span className="text-xs font-mono text-slate-300 uppercase tracking-widest">{t('architecture.visual.engine')}</span>
        </div>
        <div className="text-xs font-mono text-slate-500">/automations/active</div>
      </div>
      <div className="flex-1 relative">
        <svg className="absolute inset-0 w-full h-full">
          {nodes.slice(1).map((n) => (
            <line
              key={n.id}
              x1="50%" y1="50%"
              x2={`${n.x}%`} y2={`${n.y}%`}
              stroke={n.active ? "#ef4444" : "#334155"}
              strokeWidth={n.active ? "2" : "1"}
              strokeDasharray="4 4"
              className={n.active ? "animate-pulse" : ""}
            />
          ))}
        </svg>
        {nodes.map(n => (
          <div
            key={n.id}
            className={`absolute w-12 h-12 rounded-lg border flex items-center justify-center transition-all duration-500 transform -translate-x-1/2 -translate-y-1/2 z-10
                  ${n.id === 0 ? 'bg-[#0B1121] border-clever-teal shadow-[0_0_30px_rgba(220,38,38,0.5)] scale-125' :
                n.active ? 'bg-slate-800 border-white text-white' : 'bg-slate-900 border-slate-800 text-slate-600'}
               `}
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            {n.id === 0 ? <Bot size={24} className="text-clever-teal" /> :
              n.type === 'MAIL' ? <Mail size={16} /> :
                n.type === 'CRM' ? <Users size={16} /> :
                  n.type === 'DOCS' ? <FileText size={16} /> :
                    n.type === 'CHAT' ? <MessageSquare size={16} /> :
                      <Network size={16} />
            }
          </div>
        ))}
      </div>
      <div className="mt-auto pt-6 flex justify-between text-[10px] font-mono text-slate-500 border-t border-slate-700 relative z-10">
        <span>{t('architecture.visual.tasks')}</span>
        <span className="text-clever-teal">{t('architecture.visual.status')}</span>
      </div>
    </div>
  );
};

export const CustomArchitecturePage: React.FC<CustomArchitecturePageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900">
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gray-100 rounded-full blur-[120px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-center relative z-10">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-block px-4 py-2 bg-gray-100 text-gray-700 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-gray-200">
                {t('architecture.hero.tagline')}
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-[0.95] mb-8">
                {t('architecture.hero.title_line1')} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-clever-teal to-orange-600">{t('architecture.hero.title_line2')}</span>
              </h1>
              <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-xl">
                {t('architecture.hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4 mb-12">
                <button onClick={() => setIsModalOpen(true)} className="bg-clever-teal text-white px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-red-800 transition-all shadow-xl rounded-sm">{t('architecture.hero.cta_analysis')}</button>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 relative flex items-center justify-center py-12">
            <Reveal delay={0.2}><div className="w-full min-h-[350px] md:min-h-[400px]"><WorkflowCoreVisual /></div></Reveal>
          </div>
        </div>
      </section>
      <CustomProcess />
      <CustomCaseStudies />
      <LeadFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} sector="Custom Architecture" />
    </div>
  );
};
